import { time } from '@/lib/time'
import { SALARY_WORK_TIME_QUERY } from '@/modules/companies/queries'
import type { SalaryModuleConfigNode } from '@/modules/companies/types.graphql'
import { SalaryType } from '@/modules/companies/types.graphql'
import { FixedHourlyAddons } from '@/modules/salary/FixedHourlyAddons'
import { useBreadcrumbs, useParam } from '@/utils/hooks'
import { getFullMinutes } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { Loader } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { format, setSeconds } from 'date-fns'
import floor from 'lodash/floor'
import { Fragment, useEffect, useEffectEvent, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useSalaryModuleTranslations } from '../../hooks'
import { useSalaryMutations } from '../../util'
import type { SalarySettingsForm } from './SalarySettingsFormView'
import { SalarySettingsFormView } from './SalarySettingsFormView'
import {
  overtimeLimitsFromNode,
  overtimeLimitsToMutation,
} from './helpers/overtime-limits'
import { H2 } from '@/components'
import { FixedSalaryAddons } from '@/modules/salary/FixedSalaryAddons'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

interface EditSalaryWorkTimeProps {}

export const EditSalaryWorkTime: React.FC<EditSalaryWorkTimeProps> = () => {
  const translations = useTranslate({
    editSalarySettings: ['settings.edit-salary-settings-type', { module: '' }],
    createSalarySettings: ['settings.create-salary-settings', { module: '' }],
    otherAddons: 'settings.other-addons',

    validation: {
      driverPause: 'settings.validation.salary-driver-pause-length',
      invalid: 'common.invalid',
      priority: ['settings.validation.salary-priority-unique', { module: '' }],
      required: 'common.required',
    },
  })

  const navigate = useNavigate()

  const getModuleTranslation = useSalaryModuleTranslations()

  const salaryWorkTimeId = useParam('salaryWorkTimeId')
  const [loadComplete, setLoadComplete] = useState(false)
  const { data, loading } = useQuery(SALARY_WORK_TIME_QUERY, {
    variables: {
      id: salaryWorkTimeId,
    },
    onCompleted: data => {
      const initialData: SalarySettingsForm = {
        userId: data.salaryWorkTime.user?.id ?? '',
        salaryGroups:
          data.salaryWorkTime.salaryGroups?.edges.map(
            edge => edge?.node?.id ?? ''
          ) ?? [],
        defaultSalaryCode: data.salaryWorkTime.defaultSalaryCode?.id ?? null,
        defaultSalaryCodeWhenOvertime:
          data.salaryWorkTime.defaultSalaryCodeWhenOvertime?.id ?? null,
        absenceHourTransactionOvertimeSalaryCode:
          data.salaryWorkTime.absenceHourTransactionOvertimeSalaryCode?.id ??
          null,
        drivingSalaryCode: data.salaryWorkTime.drivingSalaryCode?.id ?? null,
        otherWorkSalaryCode:
          data.salaryWorkTime.otherWorkSalaryCode?.id ?? null,
        availabilitySalaryCode:
          data.salaryWorkTime.availabilitySalaryCode?.id ?? null,
        salaryType: data.salaryWorkTime.salaryType ?? SalaryType.HOUR,
        salaryDayCode: data.salaryWorkTime.salaryDayCode?.id ?? null,
        dayOvertimeSalaryCode:
          data.salaryWorkTime.dayOvertimeSalaryCode?.id ?? null,
        weekOvertimeSalaryCode:
          data.salaryWorkTime.weekOvertimeSalaryCode?.id ?? null,
        dailyWorkLimit: floor(
          (data.salaryWorkTime.dailyWorkLimit ?? 28800) / 3600,
          2
        ),
        periodWorkLimit: floor(
          (data.salaryWorkTime.periodWorkLimit ?? 144000) / 3600,
          2
        ),
        pauseDuration: secondsToDate(data.salaryWorkTime.pauseDuration ?? 2700),
        pauseThreshold: secondsToDate(
          data.salaryWorkTime.pauseThreshold ?? 14400
        ),
        subtractPaidPauseAfterTotalWorkThreshold:
          data.salaryWorkTime.subtractPaidPauseAfterTotalWorkThreshold ?? false,
        paidPauseSalaryCode:
          data.salaryWorkTime.paidPauseSalaryCode?.id ?? null,
        countPaidPauseInTotalWork:
          data.salaryWorkTime.countPaidPauseInTotalWork ?? false,
        paidPauseThreshold: secondsToDate(
          data.salaryWorkTime.paidPauseThreshold ?? 0
        ),
        salaryDateStart: data.salaryWorkTime.salaryDateStart ?? 26,
        salaryDateEnd: data.salaryWorkTime.salaryDateEnd ?? 25,
        name: data.salaryWorkTime.name ?? '',
        useSalaryDayPayment: data.salaryWorkTime.useSalaryDayPayment ?? false,

        useDayOvertime: data.salaryWorkTime.useDayOvertime,
        useWeekOvertime: data.salaryWorkTime.useWeekOvertime,
        useMonthOvertime: data.salaryWorkTime.useMonthOvertime,
        usePlannerForOvertime:
          data.salaryWorkTime.usePlannerForOvertime ?? false,
        usePlannerForUndertime:
          data.salaryWorkTime.usePlannerForUndertime ?? false,
        undertimeLimit: floor(
          (data.salaryWorkTime.undertimeLimit ?? 0) / 3600,
          2
        ),
        undertimeSalaryCodes:
          data.salaryWorkTime.undertimeSalaryCodes?.map(node => node.id) ?? [],

        calculateOvertimeAsAddon: data.salaryWorkTime.addOvertimeAsAddon,

        calculateWeekOvertimeInline:
          data.salaryWorkTime.calculateWeekOvertimeInline ?? true,
        monthOvertimeSalaryCode:
          data.salaryWorkTime.monthOvertimeSalaryCode?.id ?? null,
        groupSalaryOnVehicle: data.salaryWorkTime.groupSalaryOnVehicle ?? false,
        useAutomaticDietGeneration:
          data.salaryWorkTime.useAutomaticDietGeneration ?? false,
        useAutomaticSalaryDayGeneration:
          data.salaryWorkTime.useAutomaticSalaryDayGeneration ?? false,
        periodAddons:
          data.salaryWorkTime.periodAddons.map(periodAddonNode => {
            const { __typename, ...periodAddon } = periodAddonNode
            return {
              ...periodAddon,
              salaryCode: periodAddon.salaryCode?.id ?? '',
              start: time(periodAddon.start).toDate(new Date()),
              end: time(periodAddon.end).toDate(new Date()),
              salaryCodeName: periodAddon.salaryCode?.name ?? '',
              alwaysActive: false,
            }
          }) ?? [],
        periodOvertimes:
          data.salaryWorkTime.periodOvertimes.map(periodOvertimeNode => {
            const { __typename, ...periodOvertime } = periodOvertimeNode
            return {
              ...periodOvertime,
              salaryCode: periodOvertime.salaryCode?.id ?? '',
              start: time(periodOvertime.start).toDate(new Date()),
              end: time(periodOvertime.end).toDate(new Date()),
              salaryCodeName: periodOvertime.salaryCode?.name ?? '',
              alwaysActive: periodOvertime.alwaysActive ?? false,
              override: false,
              excludeIfOvertime: false,
            }
          }) ?? [],
        overtimeLimits: overtimeLimitsFromNode(data.salaryWorkTime),
        defaultDepartment: data.salaryWorkTime.defaultDepartment?.id ?? '',
        availableDepartments:
          data.salaryWorkTime.availableDepartments?.map(node => node.id) ?? [],
        availableAddons:
          data.salaryWorkTime.selectableSalaryCodes?.map(node => node.id) ?? [],
        holidayAllowances:
          data.salaryWorkTime.holidayAllowances?.map(node => node.id) ?? [],
      }
      form.updateForm(initialData)
      form.updateInitialValues(initialData)
      setLoadComplete(true)
    },
    onError() {
      void navigate(-1)
    },
  })
  const salaryWorkTime = data?.salaryWorkTime
  const config = salaryWorkTime?.moduleConfig

  const [breadcrumbsOverridden, setBreadcrumbsOverridden] = useState(false)
  const { setOverride } = useBreadcrumbs()

  const mutations = useSalaryMutations()

  const secondsToDate = (seconds: number) =>
    setSeconds(new Date(1993, 0, 9), seconds)

  const form = useForm<SalarySettingsForm>({
    values: {
      userId: salaryWorkTime?.user?.id ?? '',
      salaryGroups:
        salaryWorkTime?.salaryGroups?.edges.map(edge => edge?.node?.id ?? '') ??
        [],

      salaryType: salaryWorkTime?.salaryType ?? SalaryType.HOUR,
      defaultSalaryCode: salaryWorkTime?.defaultSalaryCode?.id ?? '',
      defaultSalaryCodeWhenOvertime:
        salaryWorkTime?.defaultSalaryCodeWhenOvertime?.id ?? null,
      absenceHourTransactionOvertimeSalaryCode:
        salaryWorkTime?.absenceHourTransactionOvertimeSalaryCode?.id ?? null,
      salaryDayCode: salaryWorkTime?.salaryDayCode?.id ?? '',
      dayOvertimeSalaryCode: salaryWorkTime?.dayOvertimeSalaryCode?.id ?? '',

      useDayOvertime: salaryWorkTime?.useDayOvertime ?? true,
      useWeekOvertime: salaryWorkTime?.useWeekOvertime ?? true,
      useMonthOvertime: salaryWorkTime?.useMonthOvertime ?? false,
      usePlannerForOvertime: salaryWorkTime?.usePlannerForOvertime ?? false,
      usePlannerForUndertime: salaryWorkTime?.usePlannerForUndertime ?? false,
      undertimeLimit: floor((salaryWorkTime?.undertimeLimit ?? 0) / 3600, 2),
      undertimeSalaryCodes:
        salaryWorkTime?.undertimeSalaryCodes?.map(node => node.id) ?? [],

      calculateOvertimeAsAddon: salaryWorkTime?.addOvertimeAsAddon ?? false,

      calculateWeekOvertimeInline:
        salaryWorkTime?.calculateWeekOvertimeInline ?? true,
      weekOvertimeSalaryCode: salaryWorkTime?.weekOvertimeSalaryCode?.id ?? '',

      monthOvertimeSalaryCode:
        salaryWorkTime?.monthOvertimeSalaryCode?.id ?? '',

      dailyWorkLimit: floor(
        (salaryWorkTime?.dailyWorkLimit ?? 28800) / 3600,
        2
      ),
      periodWorkLimit: floor(
        (salaryWorkTime?.periodWorkLimit ?? 144000) / 3600,
        2
      ),

      pauseDuration: secondsToDate(salaryWorkTime?.pauseDuration ?? 2700),
      pauseThreshold: secondsToDate(salaryWorkTime?.pauseThreshold ?? 14400),
      countPaidPauseInTotalWork:
        salaryWorkTime?.countPaidPauseInTotalWork ?? false,
      paidPauseThreshold: secondsToDate(
        salaryWorkTime?.paidPauseThreshold ?? 0
      ),
      subtractPaidPauseAfterTotalWorkThreshold:
        salaryWorkTime?.subtractPaidPauseAfterTotalWorkThreshold ?? false,
      paidPauseSalaryCode: salaryWorkTime?.paidPauseSalaryCode?.id ?? '',
      drivingSalaryCode: salaryWorkTime?.drivingSalaryCode?.id ?? '',
      otherWorkSalaryCode: salaryWorkTime?.otherWorkSalaryCode?.id ?? '',
      availabilitySalaryCode: salaryWorkTime?.availabilitySalaryCode?.id ?? '',
      salaryDateStart: salaryWorkTime?.salaryDateStart ?? 26,
      salaryDateEnd: salaryWorkTime?.salaryDateEnd ?? 25,
      name: salaryWorkTime?.name ?? '',
      groupSalaryOnVehicle: salaryWorkTime?.groupSalaryOnVehicle ?? false,
      useSalaryDayPayment: false,

      periodAddons:
        salaryWorkTime?.periodAddons.map(periodAddonNode => {
          const { __typename, ...periodAddon } = periodAddonNode
          return {
            ...periodAddon,
            salaryCode: periodAddon.salaryCode?.id ?? '',
            start: time(periodAddon.start).toDate(new Date()),
            end: time(periodAddon.end).toDate(new Date()),
            salaryCodeName: periodAddon.salaryCode?.name ?? '',
            alwaysActive: false,
          }
        }) ?? [],
      periodOvertimes:
        salaryWorkTime?.periodOvertimes.map(periodOvertimeNode => {
          const { __typename, ...periodOvertime } = periodOvertimeNode
          return {
            ...periodOvertime,
            salaryCode: periodOvertime.salaryCode?.id ?? '',
            start: time(periodOvertime.start).toDate(new Date()),
            end: time(periodOvertime.end).toDate(new Date()),
            salaryCodeName: periodOvertime.salaryCode?.name ?? '',
            override: false,
            excludeIfOvertime: false,
            alwaysActive: periodOvertime.alwaysActive ?? false,
          }
        }) ?? [],
      overtimeLimits: overtimeLimitsFromNode(salaryWorkTime),
      defaultDepartment: salaryWorkTime?.defaultDepartment?.id ?? '',
      availableDepartments:
        salaryWorkTime?.availableDepartments?.map(node => node.id) ?? [],
      availableAddons:
        salaryWorkTime?.selectableSalaryCodes?.map(node => node.id) ?? [],
      useAutomaticDietGeneration:
        salaryWorkTime?.useAutomaticDietGeneration ?? false,
      useAutomaticSalaryDayGeneration:
        salaryWorkTime?.useAutomaticSalaryDayGeneration ?? false,
      holidayAllowances:
        salaryWorkTime?.holidayAllowances?.map(node => node.id) ?? [],
    },
    validators: {
      salaryDateStart: (val, { salaryDateEnd }) =>
        val === 1 && salaryDateEnd === 31
          ? null
          : val > salaryDateEnd
          ? null
          : translations.validation.invalid,
    },
    config: {
      validateImmediately: true,
    },
  })

  async function handleEdit() {
    void mutations.patch({
      variables: {
        id: data?.salaryWorkTime.id ?? '',
        input: {
          user: form.formValues.userId,
          salaryGroups: form.formValues.salaryGroups,
          salaryType: form.formValues.salaryType,
          defaultSalaryCode: form.formValues.defaultSalaryCode,
          defaultSalaryCodeWhenOvertime:
            form.formValues.defaultSalaryCodeWhenOvertime,
          absenceHourTransactionOvertimeSalaryCode:
            form.formValues.absenceHourTransactionOvertimeSalaryCode,
          drivingSalaryCode: form.formValues.drivingSalaryCode,
          otherWorkSalaryCode: form.formValues.otherWorkSalaryCode,
          availabilitySalaryCode: form.formValues.availabilitySalaryCode,
          salaryDayCode: form.formValues.salaryDayCode,
          dayOvertimeSalaryCode: form.formValues.dayOvertimeSalaryCode,
          addOvertimeAsAddon: form.formValues.calculateOvertimeAsAddon,

          useDayOvertime: form.formValues.useDayOvertime,
          useWeekOvertime: form.formValues.useWeekOvertime,
          useMonthOvertime: form.formValues.useMonthOvertime,
          usePlannerForOvertime: form.formValues.usePlannerForOvertime,
          usePlannerForUndertime: form.formValues.usePlannerForUndertime,
          undertimeLimit: form.formValues.undertimeLimit,
          undertimeSalaryCodes: form.formValues.undertimeSalaryCodes,

          calculateWeekOvertimeInline:
            form.formValues.calculateWeekOvertimeInline,
          weekOvertimeSalaryCode: form.formValues.weekOvertimeSalaryCode,
          monthOvertimeSalaryCode: form.formValues.monthOvertimeSalaryCode,
          dailyWorkLimit: form.formValues.dailyWorkLimit,
          periodWorkLimit: form.formValues.periodWorkLimit,
          pauseDuration: getFullMinutes(form.formValues.pauseDuration),
          pauseThreshold: getFullMinutes(form.formValues.pauseThreshold),
          countPaidPauseInTotalWork: form.formValues.countPaidPauseInTotalWork,
          paidPauseThreshold: getFullMinutes(
            form.formValues.paidPauseThreshold
          ),
          subtractPaidPauseAfterTotalWorkThreshold:
            form.formValues.subtractPaidPauseAfterTotalWorkThreshold,
          paidPauseSalaryCode: form.formValues.paidPauseSalaryCode,
          salaryDateStart: form.formValues.salaryDateStart,
          salaryDateEnd: form.formValues.salaryDateEnd,
          name: form.formValues.name,
          groupSalaryOnVehicle: form.formValues.groupSalaryOnVehicle,
          useSalaryDayPayment: form.formValues.useSalaryDayPayment,
          useAutomaticDietGeneration:
            form.formValues.useAutomaticDietGeneration,
          useAutomaticSalaryDayGeneration:
            form.formValues.useAutomaticSalaryDayGeneration,
          periodAddonsManual: form.formValues.periodAddons.map(periodAddon => {
            const { id, salaryCodeName, alwaysActive, ...periodAddonInput } =
              periodAddon
            return {
              ...periodAddonInput,
              start: format(periodAddon.start, 'HH:mm'),
              end: format(periodAddon.end, 'HH:mm'),
            }
          }),
          periodOvertimesManual: form.formValues.periodOvertimes.map(
            periodOvertime => {
              const { id, salaryCodeName, ...periodOvertimeInput } =
                periodOvertime
              return {
                ...periodOvertimeInput,
                start: format(periodOvertime.start, 'HH:mm'),
                end: format(periodOvertime.end, 'HH:mm'),
              }
            }
          ),
          // overtimeLimitsManual not yet in generated GraphQL types; cast below.
          ...({
            overtimeLimitsManual: overtimeLimitsToMutation(
              form.formValues.useDayOvertime,
              form.formValues.overtimeLimits,
              form.formValues.useWeekOvertime,
              form.formValues.calculateWeekOvertimeInline,
              form.formValues.usePlannerForOvertime
            ),
          } as Record<string, unknown>),
          defaultDepartment: form.formValues.defaultDepartment,
          availableDepartments: form.formValues.availableDepartments,
          selectableSalaryCodes: form.formValues.availableAddons,
          holidayAllowances: form.formValues.holidayAllowances,
        },
      },
    })
  }

  const title = config?.name ? (
    translations.editSalarySettings({
      module: getModuleTranslation(config.name),
    })
  ) : (
    <Loader.Dots />
  )

  const handleBreadcrumbOverrides = useEffectEvent(() => {
    if (!salaryWorkTime?.user) return
    setOverride(salaryWorkTime.user.id, salaryWorkTime.user.fullName)
    setBreadcrumbsOverridden(true)
  })

  useEffect(() => {
    if (!breadcrumbsOverridden) handleBreadcrumbOverrides()
  }, [breadcrumbsOverridden])

  const UserWrapper = form.formValues.userId ? Wrapper : Fragment

  return (
    <UserWrapper>
      <H2>{title}</H2>

      {config && !loading && loadComplete && (
        <div>
          <SalarySettingsFormView
            form={form}
            // TODO: Fix this type assertion
            config={config as SalaryModuleConfigNode}
            onSubmit={handleEdit}
          />

          <H2>{translations.otherAddons}</H2>

          {!form.formValues.userId && (
            <>
              <FixedHourlyAddons
                type="salaryWorkTime"
                typeId={salaryWorkTime.id}
              />
              <FixedSalaryAddons
                type="salaryWorkTime"
                typeId={salaryWorkTime.id}
              />
            </>
          )}
        </div>
      )}
    </UserWrapper>
  )
}
